import React from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import CheckIcon from '@atlaskit/icon/core/check-mark';
import { ZoomIn } from '@atlaskit/motion';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Pressable, xcss } from '@atlaskit/primitives';
import type { IconColor } from '@atlaskit/tokens/css-type-schema';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { ColorTile, type ColorTileProps } from '../../../../common/ui/color-tile';
import { getMostReadableColor } from '../../../../common/utils';

import { messages } from './messages';

export type PressableColorTileProps = Pick<ColorTileProps, 'builtInColorMap' | 'value'> & {
	analyticsSource?: string;
	isSelected: boolean;
	onChange(color: string): void;
};

const checkIconContainerStyles = xcss({
	position: 'absolute',
});

const pressableStyles = xcss({
	background: 'none',
	alignItems: 'center',
	display: 'flex',
	height: '24px',
	justifyContent: 'center',
	width: '24px',
});

export const PressableColorTile = ({
	analyticsSource,
	builtInColorMap,
	isSelected,
	onChange,
	value,
}: PressableColorTileProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onPressableClick = () => {
		onChange(value);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'SelectItem',
				actionSubjectId: 'curatedPalette',
				source: analyticsSource,
			},
		}).fire();
	};

	const colorName = builtInColorMap.humanReadableColors.get(value);

	return (
		<Pressable aria-pressed={isSelected} onClick={onPressableClick} xcss={pressableStyles}>
			<Flex>
				<ColorTile builtInColorMap={builtInColorMap} value={value} />
				{isSelected && (
					<ZoomIn>
						{(props) => (
							<Box {...props} xcss={checkIconContainerStyles}>
								<CheckIcon
									label={formatMessage(messages.checkIconLabel)}
									color={getMostReadableColor(value) as IconColor}
									spacing="spacious"
								/>
							</Box>
						)}
					</ZoomIn>
				)}
			</Flex>
			<VisuallyHidden>{colorName ? formatMessage(colorName) : value}</VisuallyHidden>
		</Pressable>
	);
};
